import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { Gamepad2, Users, TrendingUp, Sparkles, Grid3X3, ArrowRight } from 'lucide-react';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { supabase } from '@/integrations/supabase/client';
import { SEO } from '@/components/SEO';

interface Category {
  id: string;
  name: string;
  slug: string;
  description: string;
  games_count: number;
  image_url?: string;
  icon?: string;
  color: string;
  created_at: string;
}

const CategoriesPage = () => {
  const { data: categories = [], isLoading, error } = useQuery({
    queryKey: ['categories'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('categories')
        .select('*')
        .order('games_count', { ascending: false });
      if (error) throw error;
      return data || [];
    },
  });

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 via-white to-purple-50">
        <Card className="p-8 text-center shadow-xl border-0">
          <div className="w-16 h-16 mx-auto mb-4 bg-red-100 rounded-2xl flex items-center justify-center">
            <TrendingUp className="w-8 h-8 text-red-600" />
          </div>
          <h2 className="text-xl font-semibold text-red-600 mb-2">Error Loading Categories</h2>
          <p className="text-gray-600">Please try again later.</p>
        </Card>
      </div>
    );
  }

  return (
    <>
      <SEO 
        pageType="category"
        title="Game Categories - Browse Games by Type"
        description="Explore our extensive collection of game categories. Find action, adventure, puzzle, racing, and more game types."
        keywords="game categories, online games, browser games, free games"
      />
      
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-purple-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
          {/* Enhanced Header */}
          <div className="text-center mb-16">
            <div className="inline-flex items-center gap-2 mb-6">
              <div className="p-2 bg-gradient-to-r from-blue-600 to-purple-600 rounded-xl">
                <Grid3X3 className="w-6 h-6 text-white" />
              </div>
              <span className="text-sm font-semibold text-blue-600 uppercase tracking-wide">Browse Games</span>
            </div>
            
            <h1 className="text-5xl md:text-6xl font-bold text-gray-900 mb-6">
              Game <span className="bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">Categories</span>
            </h1>
            
            <p className="text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
              Discover thousands of free online games organized by categories. Find your favorite type of game and start playing instantly!
            </p>

            {/* Stats */}
            <div className="flex items-center justify-center gap-8 mt-8">
              <div className="text-center">
                <div className="text-2xl font-bold text-gray-900">{categories.length}</div>
                <div className="text-sm text-gray-600">Categories</div>
              </div>
              <div className="w-px h-8 bg-gray-300"></div>
              <div className="text-center">
                <div className="text-2xl font-bold text-gray-900">
                  {categories.reduce((sum, cat) => sum + (cat.games_count || 0), 0)}
                </div>
                <div className="text-sm text-gray-600">Games</div>
              </div>
            </div>
          </div>

          {/* Loading State */}
          {isLoading ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
              {[...Array(8)].map((_, i) => (
                <Card key={i} className="p-6 animate-pulse border-0 shadow-lg">
                  <div className="flex items-center space-x-4 mb-4">
                    <div className="w-12 h-12 bg-gray-200 rounded-2xl"></div>
                    <div className="flex-1">
                      <div className="h-4 bg-gray-200 rounded mb-2"></div>
                      <div className="h-3 bg-gray-200 rounded w-2/3"></div>
                    </div>
                  </div>
                  <div className="h-16 bg-gray-200 rounded mb-4"></div>
                  <div className="h-3 bg-gray-200 rounded w-1/2"></div>
                </Card>
              ))}
            </div>
          ) : categories.length === 0 ? (
            <div className="text-center py-16">
              <div className="w-24 h-24 mx-auto mb-6 bg-gradient-to-br from-blue-100 to-purple-100 rounded-3xl flex items-center justify-center">
                <Gamepad2 className="w-12 h-12 text-gray-400" />
              </div>
              <h3 className="text-xl font-semibold text-gray-600 mb-2">No Categories Found</h3>
              <p className="text-gray-500">Categories will appear here once they're added.</p>
            </div>
          ) : (
            /* Enhanced Categories Grid */
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
              {categories.map((category: Category, index) => (
                <Link
                  key={category.id}
                  to={`/category/${category.slug}`}
                  className="group animate-fade-in"
                  style={{ animationDelay: `${index * 0.1}s` }}
                >
                  <Card className="h-full overflow-hidden hover:shadow-2xl transition-all duration-500 transform hover:-translate-y-2 bg-white border-0 shadow-lg group">
                    {/* Header with gradient and icon */}
                    <div 
                      className="relative h-24 flex items-center justify-center overflow-hidden"
                      style={{ background: `linear-gradient(135deg, ${category.color}, ${category.color}CC)` }}
                    >
                      {/* Pattern overlay */}
                      <div className="absolute inset-0 opacity-10">
                        <div className="absolute inset-0" style={{
                          backgroundImage: `radial-gradient(circle at 25% 25%, white 2px, transparent 2px)`,
                          backgroundSize: '20px 20px'
                        }}></div>
                      </div>
                      
                      {/* Icon */}
                      <div className="relative text-4xl text-white transform transition-transform duration-300 group-hover:scale-110 group-hover:rotate-3">
                        {category.icon || <Gamepad2 className="w-10 h-10" />}
                      </div>

                      {/* Sparkle effect */}
                      <Sparkles className="absolute top-2 right-2 w-4 h-4 text-white opacity-60 animate-pulse" />
                    </div>

                    {/* Content */}
                    <div className="p-6 space-y-4">
                      <div>
                        <h3 className="text-lg font-bold text-gray-900 group-hover:text-blue-600 transition-colors mb-2">
                          {category.name}
                        </h3>
                        
                        {category.description && (
                          <p className="text-gray-600 text-sm line-clamp-3 leading-relaxed">
                            {category.description}
                          </p>
                        )}
                      </div>

                      {/* Stats and badges */}
                      <div className="flex items-center justify-between">
                        <Badge variant="secondary" className="flex items-center gap-1">
                          <Users className="w-3 h-3" />
                          {category.games_count || 0} games
                        </Badge>
                        
                        <div className="flex items-center space-x-1 text-blue-600 group-hover:text-purple-600 transition-colors opacity-0 group-hover:opacity-100 transform translate-x-2 group-hover:translate-x-0 transition-all duration-300">
                          <span className="text-sm font-medium">Explore</span>
                          <ArrowRight className="w-4 h-4" />
                        </div>
                      </div>
                    </div>

                    {/* Bottom accent */}
                    <div 
                      className="h-1 w-0 group-hover:w-full transition-all duration-500"
                      style={{ backgroundColor: category.color }}
                    ></div>
                  </Card>
                </Link>
              ))}
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default CategoriesPage;
